/**
 * Feedback Widget Configuration for Acquiva
 * 
 * This configuration defines:
 * - GitHub integration for issue creation
 * - Help content for different pages
 * - Widget styling and behavior
 */
export const feedbackConfig = {
  // GitHub Configuration
  // Set REACT_APP_GITHUB_TOKEN, REACT_APP_GITHUB_OWNER, REACT_APP_GITHUB_REPO in your .env
  github: {
    owner: process.env.REACT_APP_GITHUB_OWNER || 'ghowett',
    repo: process.env.REACT_APP_GITHUB_REPO || 'acquiva',
    labels: {
      feedback: ['feedback', 'user-submitted'],
      bug: ['bug', 'user-reported', 'needs-triage'],
      idea: ['enhancement', 'user-idea'],
      other: ['needs-triage'],
    },
  },

  // Position of the widget
  position: 'bottom-right',

  // Primary color (violet-600)
  primaryColor: '#7c3aed',

  // Help content for different pages
  help: {
    // Page-specific help
    pages: {
      // Dashboard
      '/dashboard': {
        title: 'Dashboard Help',
        content: 'Your dashboard shows an overview of your verification activities. View recent requests, client files, and key metrics at a glance.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },

      // Clients
      '/clients': {
        title: 'Client Files Help',
        content: 'Manage your client verification files. View verification status, risk assessments, and compliance documentation for each client.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },
      '/client-files/*': {
        title: 'Client File Details',
        content: 'View detailed information about this client file including verification evidence, risk assessment, and audit trail.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },

      // Requests
      '/requests': {
        title: 'Verification Requests',
        content: 'View all your verification requests (invites). Track the status of KYC/KYB requests sent to clients.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },
      '/requests/new': {
        title: 'Create New Request',
        content: 'Send a verification request to a client. Choose between KYC (individual) or KYB (business) verification and select the appropriate level.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },

      // Reports
      '/reports': {
        title: 'Reports Help',
        content: 'Generate compliance reports and export verification data. Track completion rates and demonstrate your compliance posture.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },

      // Settings
      '/settings': {
        title: 'Settings Help',
        content: 'Manage your company settings, user permissions, email templates, and integration configurations.',
        docsUrl: '/help',
        contactEmail: 'support@acquiva.com',
      },

      // Marketing pages
      '/': {
        title: 'Welcome to Acquiva',
        content: 'Acquiva streamlines client due diligence with fast, audit-grade KYC/KYB verification. Learn more about our features and pricing.',
        docsUrl: '/features',
        contactEmail: 'hello@acquiva.com',
      },
      '/features': {
        title: 'Features Overview',
        content: 'Explore Acquiva\'s comprehensive verification features including automated checks, audit trails, and compliance reporting.',
        docsUrl: '/features',
        contactEmail: 'hello@acquiva.com',
      },
      '/pricing': {
        title: 'Pricing Information',
        content: 'Compare our pricing plans to find the right fit for your company. All plans include core features with different usage limits.',
        docsUrl: '/pricing',
        contactEmail: 'hello@acquiva.com',
      },
      '/about': {
        title: 'About Acquiva',
        content: 'Learn about our mission to make client verification fast, compliant, and user-friendly for professional services companies.',
        docsUrl: '/about',
        contactEmail: 'hello@acquiva.com',
      },
      '/contact': {
        title: 'Contact Us',
        content: 'Get in touch with our team. We\'re here to answer questions and help you get started with Acquiva.',
        contactEmail: 'hello@acquiva.com',
      },
    },

    // Default help for pages not listed above
    default: {
      title: 'Need Help?',
      content: 'We\'re here to help you get the most out of Acquiva. Check out our documentation or reach out to our support team.',
      docsUrl: '/help',
      contactEmail: 'support@acquiva.com',
    },
  },

  // Optional callbacks
  onFeedbackSubmit: (feedback) => {
    console.log('Feedback submitted:', feedback.type, feedback.title);
  },
  onHelpOpen: (path) => {
    console.log('Help opened on:', path);
  },
};

/**
 * Minimal configuration for quick setup in other projects
 */
export const minimalConfig = {
  github: {
    owner: 'your-org',
    repo: 'your-repo',
  },
  help: {
    default: {
      title: 'Need Help?',
      content: 'Check out our documentation or contact support.',
      docsUrl: '/docs',
      contactEmail: 'support@example.com',
    },
  },
};

